import React, { FC, PropsWithChildren, useContext, useRef, useState } from 'react';

import { useMinimalCartUi } from 'features/cart/hooks/use-minimal-cart-ui';
import {
  FlowDirection,
  FlowStep,
  IProductWizardContext,
  IProductWizardProvider,
  PickerSelections,
  ProductWizardRewardState,
} from 'features/menu/components/product-wizard/types';
import { useSlotsSelections } from 'features/menu/components/product-wizard/use-slots-selections';
import {
  buildFlow,
  computeSelectedProduct,
  initializeSlotModifiers,
  initializeSlotSelections,
} from 'features/menu/components/product-wizard/utils';
import { useStoreContext } from 'features/store';
import { isEqual } from 'lodash-es';

import { ISanityImage } from '@rbi-ctg/menu';
import { useMainMenuContext } from 'state/menu/main-menu';
import { ILocalizedMenuEntry } from 'state/unified-menu/types';
import { useUnifiedMenuContext } from 'state/unified-menu/unified-menu-context';
import { useMenuQueryOptions } from 'state/unified-menu/use-menu-query-options';
import { isCombo, mapImageResourceToSanityImage } from 'state/unified-menu/utils';
import { MIN_CART_QUANTITY } from 'utils/cart';
import noop from 'utils/noop';
/**
 * Provides access to menu data and manage the state of the ProductDetail page.
 */
const emptyRewardState: ProductWizardRewardState = {
  hasRewardIntent: false,
  loading: false,
  unavailable: false,
};

const ProductWizardContext = React.createContext<IProductWizardContext>({
  menuObject: ({} as unknown) as IProductWizardContext['menuObject'],
  selectedProduct: null as any,
  selectedProductImage: (null as unknown) as ISanityImage,
  setSelectedProduct: () => undefined,
  productQuantity: MIN_CART_QUANTITY,
  setProductQuantity: () => undefined,
  showCloseDialog: false,
  setShowCloseDialog: () => undefined,
  pickerSelections: {},
  selectPickerAspectOption: noop,
  slotsSelections: {},
  selectComboSlotItem: noop,
  selectModifierMultiplier: noop,
  flow: [],
  saveSlotsSelectionSnapshot: noop,
  restoreSlotsSelectionSnapshot: noop,
  clearSlotsSelectionSnapshot: noop,
  hasUnsavedChanges: () => false,
  isAnySlotCustomized: () => false,
  flowDirection: 'forward',
  setFlowDirection: () => undefined,
  removeSlotSelection: noop,
  hasInAppNavigationRef: { current: false },
  rewardState: emptyRewardState,
});

export const useProductWizardContext = () => useContext(ProductWizardContext);

export const ProductWizardProvider: FC<PropsWithChildren<IProductWizardProvider>> = ({
  menuObject,
  editingCartEntry,
  rewardState = emptyRewardState,
  children,
}) => {
  const isMinimalCartUIEnabled = useMinimalCartUi();
  const { posServiceMode } = useMenuQueryOptions();
  const { indexedMenu } = useUnifiedMenuContext();
  const [flowDirection, setFlowDirection] = useState<FlowDirection>('forward');
  const [productQuantity, setProductQuantity] = useState<number>(MIN_CART_QUANTITY);
  const [pickerSelections, setPickerSelections] = useState<PickerSelections>({});
  const [selectedProduct, setSelectedProduct] = useState<ILocalizedMenuEntry>(
    () =>
      computeSelectedProduct({
        menu: indexedMenu,
        menuObject,
        posServiceMode,
        editingCartEntry,
        isMinimalCartUIEnabled,
      }) ?? menuObject
  );

  const { activeDayParts } = useMainMenuContext();
  const { store } = useStoreContext();
  const storeHasBurgersForBreakfast = !!store.hasBurgersForBreakfast;

  const { slotsSelections, setSlotsSelections, slotsSelectionsSnapshot } = useSlotsSelections({
    item: menuObject,
    selectedProduct,
    indexedMenu,
    editingCartEntry,
    posServiceMode,
    isMinimalCartUIEnabled,
    activeDayParts,
    storeHasBurgersForBreakfast,
  });

  const [showCloseDialog, setShowCloseDialog] = useState<boolean>(false);

  const [flow, setFlow] = useState<FlowStep[]>(
    buildFlow(
      menuObject,
      indexedMenu,
      pickerSelections,
      posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast
    )
  );

  const hasInAppNavigationRef = useRef<boolean>(false);

  const selectPickerAspectOption: IProductWizardContext['selectPickerAspectOption'] = (
    pickerAspectKey,
    pickerAspectOptionId
  ) => {
    setPickerSelections(prevPickerSelections => {
      const updatedSelections = {
        ...prevPickerSelections,
        [pickerAspectKey]: pickerAspectOptionId,
      };

      setFlow(
        buildFlow(
          menuObject,
          indexedMenu,
          updatedSelections,
          posServiceMode,
          activeDayParts,
          storeHasBurgersForBreakfast
        )
      );

      setSelectedProduct(prevSelectedProduct => {
        const updatedProduct =
          computeSelectedProduct({
            menu: indexedMenu,
            menuObject,
            posServiceMode,
            editingCartEntry,
            isMinimalCartUIEnabled,
            pickerSelections: updatedSelections,
            pickerAspectKey,
          }) ?? prevSelectedProduct;

        if (updatedProduct.id !== prevSelectedProduct.id) {
          setSlotsSelections(
            initializeSlotSelections(
              updatedProduct,
              indexedMenu,
              isCombo(menuObject),
              posServiceMode,
              activeDayParts,
              storeHasBurgersForBreakfast
            )
          );
          return updatedProduct;
        }
        return prevSelectedProduct;
      });

      return updatedSelections;
    });
  };

  const selectComboSlotItem: IProductWizardContext['selectComboSlotItem'] = (slotKey, itemId) => {
    setSlotsSelections(prevSlotsSelections => {
      const previousItemId = prevSlotsSelections[slotKey]?.itemId;
      const newItemWasSelected = itemId !== previousItemId;
      return {
        ...prevSlotsSelections,
        [slotKey]: {
          ...prevSlotsSelections[slotKey],
          itemId,
          ...(newItemWasSelected && {
            modifiers: initializeSlotModifiers(
              indexedMenu,
              itemId,
              posServiceMode,
              true,
              isCombo(menuObject)
            ),
          }),
        },
      };
    });
  };

  const selectModifierMultiplier: IProductWizardContext['selectModifierMultiplier'] = (
    slotKey,
    itemId,
    modifierKey,
    multipliersSelection
  ) => {
    setSlotsSelections(prevSlotsSelections => {
      const slotSelections = {
        ...prevSlotsSelections,
        [slotKey]: {
          itemId,
          modifiers: {
            ...(prevSlotsSelections[slotKey]?.modifiers ?? {}),
            [modifierKey]: multipliersSelection,
          },
        },
      };

      return slotSelections;
    });
  };

  const saveSlotsSelectionSnapshot: IProductWizardContext['saveSlotsSelectionSnapshot'] = () => {
    // We do not need to make a deep copy of the object since all operations are immutable
    slotsSelectionsSnapshot.current = slotsSelections;
  };

  const restoreSlotsSelectionSnapshot: IProductWizardContext['restoreSlotsSelectionSnapshot'] = () => {
    if (slotsSelectionsSnapshot.current) {
      setSlotsSelections(slotsSelectionsSnapshot.current);
      slotsSelectionsSnapshot.current = undefined;
    }
  };

  const clearSlotsSelectionSnapshot: IProductWizardContext['clearSlotsSelectionSnapshot'] = () => {
    slotsSelectionsSnapshot.current = undefined;
  };

  const removeSlotSelection: IProductWizardContext['removeSlotSelection'] = slotKey => {
    setSlotsSelections(prev => {
      const { [slotKey]: removed, ...remaining } = prev;
      return remaining;
    });
  };

  const hasUnsavedChanges: IProductWizardContext['hasUnsavedChanges'] = () => {
    const snapshot = slotsSelectionsSnapshot.current;
    if (!snapshot || snapshot === slotsSelections) {
      return false;
    }

    const equal = isEqual(snapshot, slotsSelections);
    return !equal;
  };

  const selectedProductImage: ISanityImage = mapImageResourceToSanityImage(
    selectedProduct.image?.resource ?? '',
    selectedProduct.image?.altText?.locale ?? ''
  );

  const isAnySlotCustomized = (): boolean => {
    const defaultSlotSelections = initializeSlotSelections(
      selectedProduct,
      indexedMenu,
      isCombo(menuObject),
      posServiceMode,
      activeDayParts,
      storeHasBurgersForBreakfast
    );

    return !isEqual(slotsSelections, defaultSlotSelections);
  };

  const value: IProductWizardContext = {
    menuObject,
    selectedProduct,
    selectedProductImage,
    setSelectedProduct,
    productQuantity,
    setProductQuantity,
    showCloseDialog,
    setShowCloseDialog,
    pickerSelections,
    selectPickerAspectOption,
    slotsSelections,
    selectComboSlotItem,
    selectModifierMultiplier,
    flow,
    saveSlotsSelectionSnapshot,
    restoreSlotsSelectionSnapshot,
    clearSlotsSelectionSnapshot,
    hasUnsavedChanges,
    isAnySlotCustomized,
    flowDirection,
    setFlowDirection,
    removeSlotSelection,
    hasInAppNavigationRef,
    rewardState,
  };

  return <ProductWizardContext.Provider value={value}>{children}</ProductWizardContext.Provider>;
};
